// Hideo - UI translations (English and Italian). Rules, API and glossary: docs/i18n.md.
// English is the source language: code and markup contain English strings, and t() returns them in the active language.
// Italian translations live in ./it/<area>.ts as { 'English source': 'Testo italiano' }.
// The language is resolved once at load time; changing it reloads the page (setLang).
import type { Dictionary, Lang, LangPref, Params } from './types'
import core from './it/core'
import cloud from './it/cloud'
import ped from './it/ped'
import seo from './it/seo'
import editor from './it/editor'
import pages from './it/pages'
import ui from './it/ui'
import help from './it/help'
import markup from './it/markup'

export type { Dictionary, Lang, LangPref, Params }

export const LANGS: readonly Lang[] = ['en', 'it']
export const LANG_KEY = 'creatorOS_lang'   // 'auto' (default) | 'en' | 'it'; same prefix as the other device keys (never rename)
// Same merge order as the old dictionary files: a later dictionary wins (tools/check.py reports conflicts)
export const I18N: { it: Dictionary } = { it: Object.assign({}, core, cloud, ped, seo, editor, pages, ui, help, markup) }

export function langPref(): LangPref {
  try {
    const v = localStorage.getItem(LANG_KEY)
    return v === 'en' || v === 'it' ? v : 'auto'
  } catch {
    return 'auto'
  }
}
// Browser language: Italian if any preferred language is Italian, English otherwise
export function detectLang(): Lang {
  const list = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language || 'en']
  return list.some(l => /^it\b/i.test(l || '')) ? 'it' : 'en'
}
const pref = langPref()
export const LANG: Lang = pref === 'auto' ? detectLang() : pref
export const LOCALE = LANG === 'it' ? 'it-IT' : 'en-GB'
document.documentElement.lang = LANG

/** Replace {name} placeholders. Values are inserted as they are: escape user data with esc() before passing it. */
export function tFill(s: string, params?: Params): string {
  return params ? s.replace(/\{(\w+)\}/g, (m, k: string) => (k in params ? String(params[k]) : m)) : s
}
/** Translate an English source string into the active language */
export function t(s: string, params?: Params): string {
  const dict = LANG === 'en' ? null : I18N[LANG]
  const r = dict && Object.prototype.hasOwnProperty.call(dict, s) ? dict[s] : s
  return tFill(r, params)
}
/** Plural: tn(n, '{n} video', '{n} videos'); the n parameter is always available */
export function tn(n: number, one: string, many: string, params?: Params): string {
  return t(n === 1 ? one : many, { n, ...(params || {}) })
}

// Stored values shown to the user (statuses, priorities, verdicts, default names…) may have been saved in either
// language. tv() shows them in the active language: an Italian value is mapped back to its English source through the
// Italian dictionary, an English value is translated as usual. Unknown values (user content) are kept.
let reverse: Record<string, string> | null = null
export function tv<T>(s: T): T | string {
  if (typeof s !== 'string' || !s) return s
  if (LANG === 'it') return t(s)
  if (!reverse) {
    reverse = {}
    for (const [en, it] of Object.entries(I18N.it)) if (!(it in reverse)) reverse[it] = en
  }
  return reverse[s] || s
}

// Static markup: elements marked with data-t (text), data-t-html (inner HTML), data-t-ph (placeholder),
// data-t-title (title), data-t-aria (aria-label) and data-t-dph (data-ph, the CSS placeholder of contenteditable fields).
// The attribute holds the English source; when it is empty the element's own English content is the source (it is
// copied into the attribute, so the call can be repeated).
export function applyI18n(root: ParentNode = document): void {
  const each = (attr: string, get: (el: Element) => string, set: (el: Element, v: string) => void) =>
    root.querySelectorAll('[' + attr + ']').forEach(el => {
      let src = el.getAttribute(attr)
      if (!src) {
        src = get(el)
        el.setAttribute(attr, src)
      }
      set(el, t(src))
    })
  each('data-t', el => (el.textContent || '').trim(), (el, v) => { el.textContent = v })
  each('data-t-html', el => el.innerHTML.trim(), (el, v) => { el.innerHTML = v })
  each('data-t-ph', el => el.getAttribute('placeholder') || '', (el, v) => el.setAttribute('placeholder', v))
  each('data-t-title', el => el.getAttribute('title') || '', (el, v) => el.setAttribute('title', v))
  each('data-t-aria', el => el.getAttribute('aria-label') || '', (el, v) => el.setAttribute('aria-label', v))
  each('data-t-dph', el => el.getAttribute('data-ph') || '', (el, v) => el.setAttribute('data-ph', v))
}

/** Change the language preference and reload, so that every page and constant is rebuilt */
export function setLang(p: LangPref): void {
  try {
    if (p === 'auto') localStorage.removeItem(LANG_KEY)
    else localStorage.setItem(LANG_KEY, p)
  } catch { /* storage unavailable: the reload keeps the automatic language */ }
  location.reload()
}

// Dates in the active language (months 0-11, weekdays Monday-first 0-6)
type NameStyle = 'long' | 'short' | 'narrow'
export const monthName = (i: number, style?: NameStyle | '') =>
  new Intl.DateTimeFormat(LOCALE, { month: style || 'long' }).format(new Date(2026, i, 1))
export const dayName = (i: number, style?: NameStyle | '') =>
  new Intl.DateTimeFormat(LOCALE, { weekday: style || 'long' }).format(new Date(2026, 0, 5 + i))
export const fmtDate = (d: Date, opts?: Intl.DateTimeFormatOptions | null) =>
  new Intl.DateTimeFormat(LOCALE, opts || { day: '2-digit', month: '2-digit', year: 'numeric' }).format(d)
/** Upper-case first letter (Italian month and day names are lower case) */
export const ucFirst = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s)

// Path of the user guide for the active language
export const GUIDE_FILE = 'guide/' + LANG + '.md'
